import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  LayoutDashboard,
  Upload,
  Users,
  Shield,
  FileText,
  LogOut,
  Map,
  Trash2,
  FileBadge,
  Settings,
  Menu,
  ChevronDown,
  Search,
  X,
  Briefcase,
  UserPlus,
  GraduationCap,
  Bell,
  UserCircle,
  HelpCircle,
  MessageSquare,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { ViewAsBanner, useViewAs } from '../context/ViewAsContext';
import { useRealtime } from '../hooks/useRealtime';
import { useTour } from '../context/TourContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout({ onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission, isAdmin, loading: permissionsLoading } = usePermissions();
  const { viewingAs, effectivePermissions, isViewingAsOther, viewAsLoading } = useViewAs();
  const { startTourForPath, showWelcome, dismissWelcome, startWelcomeTour, getAvailableTours } = useTour();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ personnel: [], agencies: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [smsUnread, setSmsUnread] = useState(0);
  const [namesPending, setNamesPending] = useState(0);
  const [allPersonnel, setAllPersonnel] = useState([]);
  const [allAgencies, setAllAgencies] = useState([]);
  const [userAdminPerms, setUserAdminPerms] = useState({});
  const [userNavPerms, setUserNavPerms] = useState({});
  const searchRef = useRef(null);

  // SIMPLE: Get nav permissions directly from effectivePermissions when viewing as
  const viewAsNavPerms = isViewingAsOther ? (effectivePermissions?.nav_permissions || {}) : null;
  const viewAsAdminPerms = isViewingAsOther ? (effectivePermissions?.admin_permissions || {}) : null;
  
  // Debug: Log effectivePermissions immediately
  if (isViewingAsOther) {
    console.log('VIEW AS - effectivePermissions:', effectivePermissions);
    console.log('VIEW AS - viewAsNavPerms:', viewAsNavPerms);
    console.log('VIEW AS - dashboard value:', viewAsNavPerms?.dashboard);
    console.log('VIEW AS - reports value:', viewAsNavPerms?.reports);
  }

  // Fetch user's permission level (only needed when NOT viewing as another user)
  useEffect(() => {
    const fetchUserPermissions = async () => {
      // Skip if viewing as - we use effectivePermissions directly
      if (isViewingAsOther) {
        return;
      }
      
      if (!user?.permission_level_id) {
        // Legacy admin or no permission level - use role-based permissions
        if (user?.role === 'admin') {
          setUserAdminPerms({
            admin_dashboard: true, admin_users: true, admin_permissions: true,
            admin_audit: true, admin_job_duties: true, admin_signatures: true,
            admin_declinations: true, admin_invoices: true, admin_integrations: true
          });
          setUserNavPerms({
            dashboard: true, personnel: true, agencies: true,
            reports: true, map: true, uploads: true
          });
        } else {
          setUserAdminPerms({});
          setUserNavPerms({});
        }
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/permission-levels/${user.permission_level_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const level = response.data;
        setUserAdminPerms(level.admin_permissions || {});
        setUserNavPerms(level.nav_permissions || {});
      } catch (error) {
        console.error('Failed to fetch permission level:', error);
        setUserAdminPerms({});
        setUserNavPerms({});
      }
    };
    
    fetchUserPermissions();
  }, [user, isViewingAsOther]);

  // Check if user has any admin access (to show Admin nav link)
  // When viewing as, check the VIEW AS user's admin perms, not the original user's
  const activeAdminPerms = isViewingAsOther ? viewAsAdminPerms : userAdminPerms;
  const hasAnyAdminAccess = Object.values(activeAdminPerms || {}).some(v => v === true);
  
  // Check if user is admin (from either permissions context or user role)
  // When viewing as another user, use their role, not the original admin's role
  const userIsAdmin = isViewingAsOther 
    ? (viewingAs?.role === 'admin')
    : (isAdmin || user?.role === 'admin');

  // SIMPLIFIED: Get the active nav permissions
  // When viewing as: use viewAsNavPerms directly from effectivePermissions
  // Otherwise: use userNavPerms from state
  const activeNavPerms = isViewingAsOther ? viewAsNavPerms : userNavPerms;
  
  // Only show loading state when NOT viewing as (viewAsNavPerms comes directly from context)
  const stillLoading = isViewingAsOther 
    ? viewAsLoading  // Only wait for ViewAs context to load
    : false;  // userNavPerms loads instantly from useEffect
  
  // SIMPLE permission checks - just check the active nav permissions
  const canViewDashboard = stillLoading ? false : (
    activeNavPerms?.dashboard === true || 
    (!isViewingAsOther && (userIsAdmin || !user?.permission_level_id))
  );
  const canViewUploads = stillLoading ? false : (
    activeNavPerms?.uploads === true || 
    (!isViewingAsOther && (userIsAdmin || hasAnyPermission([
      'upload_visits', 'upload_projections', 'upload_credentials', 
      'upload_declinations', 'upload_invoices', 'manage_uploads'
    ])))
  );
  const canViewAgencies = stillLoading ? false : (
    activeNavPerms?.agencies === true || 
    (!isViewingAsOther && (userIsAdmin || hasAnyPermission(['view_agencies', 'view_agency_details'])))
  );
  const canViewPersonnel = stillLoading ? false : (
    activeNavPerms?.personnel === true || 
    (!isViewingAsOther && (userIsAdmin || hasAnyPermission(['view_personnel', 'view_personnel_details'])))
  );
  const canViewReports = stillLoading ? false : (
    activeNavPerms?.reports === true || 
    (!isViewingAsOther && (userIsAdmin || hasAnyPermission(['view_reports', 'view_map'])))
  );
  const canViewMap = stillLoading ? false : (
    activeNavPerms?.map === true || 
    (!isViewingAsOther && (userIsAdmin || hasAnyPermission(['view_map'])))
  );
  const canViewCommunications = stillLoading ? false : (
    activeNavPerms?.communications === true ||
    (!isViewingAsOther && userIsAdmin)
  );
  const canViewAdmin = stillLoading ? false : (
    hasAnyAdminAccess || 
    (!isViewingAsOther && (userIsAdmin || hasAnyPermission([
      'view_admin_console', 'view_invoice_verification', 'view_signature_documents',
      'manage_users', 'view_declinations_admin', 'manage_permission_levels'
    ])))
  );
  
  // Debug logging for View As
  useEffect(() => {
    if (isViewingAsOther) {
      console.log('Layout View As FULL DEBUG:', JSON.stringify({
        isViewingAsOther,
        viewAsLoading,
        stillLoading,
        viewAsNavPerms,
        activeNavPerms,
        canViewDashboard,
        canViewReports,
        canViewMap,
        canViewAgencies,
        canViewPersonnel
      }));
    }
  }, [isViewingAsOther, viewAsLoading, stillLoading, viewAsNavPerms, activeNavPerms, canViewDashboard, canViewReports, canViewMap, canViewAgencies, canViewPersonnel]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Load data for search
  useEffect(() => {
    const loadSearchData = async () => {
      if (!userIsAdmin) return;
      try {
        const [personnelRes, agenciesRes] = await Promise.all([
          axios.get(`${API}/personnel`),
          axios.get(`${API}/agency-profiles`)
        ]);
        setAllPersonnel(personnelRes.data);
        setAllAgencies(agenciesRes.data);
      } catch (error) {
        console.error('Failed to load search data:', error);
      }
    };
    loadSearchData();
    
    // Also refresh when location changes (in case data was updated on another page)
    // This ensures newly created agencies are searchable
  }, [location.pathname]);

  // Fetch this user's own notification count (own credentials + signatures they must sign)
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/my/count`);
        setNotificationCount(response.data.count || 0);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotificationCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch unread SMS count for nav badge
  useEffect(() => {
    const fetchSmsUnread = async () => {
      if (!userIsAdmin) return;
      try {
        const res = await axios.get(`${API}/sms/unread-count`);
        setSmsUnread(res.data?.unread || 0);
      } catch (err) {
        // SMS unread endpoint may 503 if Twilio isn't configured — silent
      }
    };
    const fetchNamesPending = async () => {
      if (!userIsAdmin) return;
      try {
        const res = await axios.get(`${API}/name-review/summary`);
        setNamesPending(res.data?.pending || 0);
      } catch (err) { /* admin-only; silent */ }
    };
    fetchSmsUnread();
    fetchNamesPending();
    const interval = setInterval(() => { fetchSmsUnread(); fetchNamesPending(); }, 60000);
    window.addEventListener('name-review-changed', fetchNamesPending);
    return () => { clearInterval(interval); window.removeEventListener('name-review-changed', fetchNamesPending); };
  }, [userIsAdmin]);

  // Realtime updates for the SMS badge — instant when Twilio webhook fires
  useRealtime({
    'sms.unread_changed': (data) => {
      if (typeof data?.unread === 'number') setSmsUnread(data.unread);
    },
    'sms.received': () => {
      // bump immediately even if the unread_changed event arrives a beat later
      setSmsUnread(n => n + 1);
    },
  });

  // Handle search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults({ personnel: [], agencies: [] });
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const matchedPersonnel = allPersonnel
      .filter(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(query) ||
        p.first_name?.toLowerCase().includes(query) ||
        p.last_name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
      )
      .slice(0, 5);
    
    const matchedAgencies = allAgencies
      .filter(a => a.agency_name?.toLowerCase().includes(query))
      .slice(0, 5);
    
    setSearchResults({ personnel: matchedPersonnel, agencies: matchedAgencies });
  }, [searchQuery, allPersonnel, allAgencies]);

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close search on route change
  useEffect(() => {
    setShowSearchResults(false);
    setSearchQuery('');
  }, [location.pathname]);

  const handleResultClick = (type, id) => {
    if (type === 'personnel') {
      navigate(`/personnel/${id}`);
    } else {
      navigate(`/agencies/${id}`);
    }
    setShowSearchResults(false);
    setSearchQuery('');
  };

  const uploadMenuItems = [
    { name: 'Upload Final', path: '/upload', icon: Upload, permission: 'upload_visits' },
    { name: 'Upload Projections', path: '/upload-projections', icon: Upload, permission: 'upload_projections' },
    { name: 'Upload Credentials', path: '/credential-upload', icon: FileBadge, permission: 'upload_credentials' },
    { name: 'Credentials by Email', path: '/admin/credentials-email-review', icon: FileBadge, permission: 'upload_credentials' },
    { name: 'Upload Declinations', path: '/upload-declinations', icon: Upload, permission: 'upload_declinations' },
    { name: 'Declinations by Email', path: '/admin/declinations-review', icon: Upload, permission: 'view_declinations_admin' },
    { name: 'Upload Invoices', path: '/upload-invoices', icon: Upload, permission: 'upload_invoices' },
    { name: 'Upload Applicants', path: '/upload-applicants', icon: Upload, permission: 'upload_visits' },
    { name: 'Historical Weekly', path: '/upload-historical-weekly', icon: Upload, permission: 'upload_visits' },
    { name: 'Manage Uploads', path: '/manage-uploads', icon: Trash2, permission: 'manage_uploads' },
    { name: 'Import from BambooHR', path: '/import', icon: FileSpreadsheet, permission: 'upload_visits' },
  ].filter(item => userIsAdmin && (isAdmin || hasPermission(item.permission)));

  const mainNavigation = [
    { name: 'Home', path: '/home', icon: LayoutDashboard, show: !userIsAdmin },
    { name: 'My Profile', path: '/my-profile', icon: UserPlus, show: !userIsAdmin },
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, show: canViewDashboard },
    { name: 'Agencies', path: '/agencies', icon: Shield, show: canViewAgencies },
    { name: 'Personnel', path: '/personnel', icon: UserPlus, show: canViewPersonnel },
    { name: 'Communications', path: '/communications', icon: MessageSquare, show: canViewCommunications },
    { name: 'Recruiting', path: '/recruiting', icon: GraduationCap, show: canViewPersonnel },
    { name: 'Reports', path: '/reports', icon: FileText, show: canViewReports || canViewMap },
    { name: 'Admin', path: '/admin', icon: Settings, show: canViewAdmin },
  ].filter(item => item.show && (userIsAdmin || item.path === '/home' || item.path === '/my-profile'));

  const isUploadActive = uploadMenuItems.some(item => location.pathname === item.path);
  const isPersonnelActive = location.pathname.startsWith('/personnel') || location.pathname.startsWith('/ats');

  return (
    <div className={`min-h-screen bg-background ${isViewingAsOther ? 'pt-10' : ''}`}>
      {/* View As Banner - shows when impersonating another user */}
      <ViewAsBanner />
      
      <header className={`sticky ${isViewingAsOther ? 'top-10' : 'top-0'} z-50 w-full border-b bg-white/80 backdrop-blur-md`}>
        {/* Row 1: Logo + Search + Account actions */}
        <div className="container mx-auto flex h-16 md:h-20 items-center justify-between gap-2 px-4">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-shrink-0">
            {/* Mobile / Compact Menu Button — show on smaller screens (under lg) */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
            
            <Link to={userIsAdmin ? '/dashboard' : '/home'} className="flex-shrink-0">
              <img 
                src="/logo.png" 
                alt="Elite Home Rehab" 
                className="h-10 md:h-[50px] w-auto"
              />
            </Link>
          </div>
          
          {/* Search + Account actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Search */}
            <div className="relative" ref={searchRef}>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                <Input
                  type="text"
                  placeholder="Search..."
                  className="pl-8 pr-7 w-40 md:w-56 lg:w-64 xl:w-72 h-9 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSearchResults(true)}
                  data-testid="global-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setShowSearchResults(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showSearchResults && (searchResults.personnel.length > 0 || searchResults.agencies.length > 0) && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-lg border z-50 py-2 max-h-96 overflow-y-auto">
                  {searchResults.personnel.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Users size={12} />
                        Personnel
                      </div>
                      {searchResults.personnel.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handleResultClick('personnel', p.id)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-3"
                          data-testid={`search-result-personnel-${p.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-sage/10 flex items-center justify-center">
                            <Users size={14} className="text-sage" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{p.first_name} {p.last_name}</p>
                            <p className="text-xs text-muted-foreground">{p.discipline || p.personnel_type || 'Personnel'}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searchResults.agencies.length > 0 && (
                    <div className={searchResults.personnel.length > 0 ? 'border-t mt-1 pt-1' : ''}>
                      <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Shield size={12} />
                        Agencies
                      </div>
                      {searchResults.agencies.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => handleResultClick('agency', a.id)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-3"
                          data-testid={`search-result-agency-${a.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Shield size={14} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{a.agency_name}</p>
                            <p className="text-xs text-muted-foreground">{a.total_visits || 0} total visits</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* No results message */}
              {showSearchResults && searchQuery.length >= 2 && 
               searchResults.personnel.length === 0 && searchResults.agencies.length === 0 && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-lg border z-50 py-4 px-3 text-center text-sm text-muted-foreground">
                  No results found for "{searchQuery}"
                </div>
              )}
            </div>
            
            {/* Notification Bell — shown to every user; content is scoped to
                their own credentials + signatures they personally must sign. */}
            {true && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/notifications')}
                className="relative gap-1.5 px-2 text-muted-foreground hover:text-foreground"
                data-testid="notifications-button"
              >
                <Bell size={18} />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-medium">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </Button>
            )}
            
            {/* Help/Tour Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => startTourForPath(location.pathname)}
              className="gap-1.5 px-2 text-muted-foreground hover:text-foreground"
              data-testid="help-tour-button"
              title="Page Guide"
            >
              <HelpCircle size={18} />
            </Button>

            <Link to="/my-profile">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 px-2.5 text-muted-foreground hover:text-foreground"
                data-testid="my-profile-button"
              >
                <UserCircle size={16} />
                <span className="hidden md:inline">My Profile</span>
              </Button>
            </Link>

            <Link to="/settings">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 px-2.5 text-muted-foreground hover:text-foreground"
                data-testid="settings-button"
              >
                <Settings size={16} />
                <span className="hidden md:inline">Settings</span>
              </Button>
            </Link>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="gap-1.5 px-2.5 text-muted-foreground hover:text-foreground"
              data-testid="logout-button"
            >
              <LogOut size={16} />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>

        {/* Row 2: Main Navigation (desktop only) */}
        <div className="hidden lg:block border-t border-border/50 bg-white/60">
          <div className="container mx-auto px-4">
            <nav className="flex items-center gap-1 py-2 flex-wrap">
              {/* Dashboard - only show if user has permission */}
              {canViewDashboard && userIsAdmin && (
                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`gap-2 px-3 ${
                      location.pathname === '/dashboard'
                        ? 'bg-primary text-white'
                        : 'text-muted-foreground hover:bg-primary hover:text-white'
                    }`}
                    data-testid="nav-dashboard"
                  >
                    <LayoutDashboard size={16} />
                    <span>Dashboard</span>
                  </Button>
                </Link>
              )}

              {/* Menu Dropdown - only show if user has any upload permissions */}
              {canViewUploads && uploadMenuItems.length > 0 && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`gap-2 px-3 ${
                      isUploadActive
                        ? 'bg-primary text-white'
                        : 'text-muted-foreground hover:bg-primary hover:text-white'
                    }`}
                    onClick={() => setMenuOpen(!menuOpen)}
                    data-testid="nav-menu"
                  >
                    <Upload size={16} />
                    <span>Uploads</span>
                    <ChevronDown size={12} className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                  </Button>
                  
                  {menuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setMenuOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-lg border z-50 py-1">
                        {uploadMenuItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link 
                              key={item.path} 
                              to={item.path}
                              onClick={() => setMenuOpen(false)}
                            >
                              <div className={`flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 text-sm ${
                                isActive ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground'
                              }`}>
                                <Icon size={15} />
                                <span>{item.name}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Other nav items (skip Dashboard which is rendered separately above) */}
              {mainNavigation.filter(item => item.name !== 'Dashboard').map((item) => {
                const Icon = item.icon;
                const isActive = item.path === '/personnel' 
                  ? isPersonnelActive 
                  : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                const showBadge = item.name === 'Communications' && smsUnread > 0;
                const showReviewBadge = item.name === 'Admin' && namesPending > 0;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`gap-2 px-3 relative ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-muted-foreground hover:bg-primary hover:text-white'
                      }`}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon size={16} />
                      <span>{item.name}</span>
                      {showBadge && (
                        <span
                          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold"
                          data-testid="nav-comms-unread-badge"
                        >
                          {smsUnread > 99 ? '99+' : smsUnread}
                        </span>
                      )}
                      {showReviewBadge && (
                        <span
                          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold"
                          title={`${namesPending} uploaded name(s) need your review`}
                          data-testid="nav-admin-name-review-badge"
                        >
                          {namesPending > 99 ? '99+' : namesPending}
                        </span>
                      )}
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-72 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden overflow-y-auto ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <nav className="p-4 space-y-1">
          {/* Dashboard (admins) / Home (everyone else) */}
          <Link to={userIsAdmin ? '/dashboard' : '/home'} onClick={() => setMobileMenuOpen(false)}>
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
              location.pathname === (userIsAdmin ? '/dashboard' : '/home')
                ? 'bg-primary text-white' 
                : 'text-muted-foreground hover:bg-muted'
            }`}>
              <LayoutDashboard size={20} />
              <span className="font-medium">{userIsAdmin ? 'Dashboard' : 'Home'}</span>
            </div>
          </Link>

          {/* Uploads Section - only show if user has upload permissions */}
          {canViewUploads && uploadMenuItems.length > 0 && (
            <>
              <div className="pt-3 pb-2">
                <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Uploads</p>
              </div>
              {uploadMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                      isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'
                    }`}>
                      <Icon size={20} />
                      <span className="font-medium">{item.name}</span>
                    </div>
                  </Link>
                );
              })}
            </>
          )}

          {/* Main Navigation */}
          <div className="pt-3 pb-2">
            <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Navigation</p>
          </div>
          {mainNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/personnel' 
              ? isPersonnelActive 
              : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                  isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'
                }`}>
                  <Icon size={20} />
                  <span className="font-medium">{item.name}</span>
                </div>
              </Link>
            );
          })}

          {/* Logout */}
          <div className="pt-4 border-t mt-4">
            <button 
              onClick={() => { setMobileMenuOpen(false); onLogout(); }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </nav>
      </div>

      <main className="container mx-auto p-4 md:p-6 lg:p-8 xl:p-12">
        <Outlet />
      </main>

      {/* Welcome Tour Modal - First Login */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 text-center" data-testid="welcome-tour-modal">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <HelpCircle size={32} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Welcome!</h2>
            <p className="text-muted-foreground mb-6">
              Would you like a quick guided tour of this page? You can always access the tour later by clicking the <HelpCircle size={14} className="inline" /> button in the top menu.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={startWelcomeTour} className="w-full" data-testid="start-tour-btn">
                Yes, show me around
              </Button>
              <Button variant="ghost" onClick={dismissWelcome} className="w-full text-muted-foreground" data-testid="skip-tour-btn">
                No thanks, I'll explore on my own
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
